import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Clock, Calendar, Tag, User, ArrowRight } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import OptimizedImage from '../components/OptimizedImage';
import { blogService } from '../lib/database';
import type { BlogPost } from '../lib/supabase';
import toast from 'react-hot-toast';

const Blog = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || 'all';
  const searchParam = searchParams.get('search') || '';
  
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParam);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);

  const categories = ['all', 'Guidance', 'JEE', 'NEET', 'Career', 'Admissions', 'Scholarships', 'College Life', 'Exam Preparation'];

  useEffect(() => {
    fetchBlogPosts();
  }, [selectedCategory, searchQuery]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const filters: any = { published: true };
      
      if (selectedCategory !== 'all') {
        filters.category = selectedCategory;
      }
      
      if (searchQuery) {
        filters.search = searchQuery;
      }
      
      const data = await blogService.getAll(filters);
      setBlogPosts(data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to load blog posts');
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchBlogPosts();
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light">
        <SEOHead
          title="College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp"
          description="Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts. Stay updated with latest college admission trends and insights."
          keywords="college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling, education blog, college selection tips"
          url="/blog"
          type="website"
        />
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading blog posts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light">
      <SEOHead
        title={selectedCategory !== 'all' 
          ? `${selectedCategory} Articles & Resources | Grad Vikalp Blog`
          : "College Guidance Blog - Expert Tips & Admission Insights | Grad Vikalp"}
        description={selectedCategory !== 'all'
          ? `Read expert articles and resources about ${selectedCategory} from Grad Vikalp's education blog. Get insights, tips, and guidance for your college journey.`
          : "Get expert college guidance, admission tips, JEE & NEET preparation strategies, and career advice from our education experts. Stay updated with latest college admission trends and insights."}
        keywords={selectedCategory !== 'all'
          ? `${selectedCategory}, ${selectedCategory.toLowerCase()} guidance, ${selectedCategory.toLowerCase()} tips, college education, student resources, grad vikalp blog`
          : "college guidance blog, admission tips, JEE preparation, NEET guidance, college admission advice, career counseling, education blog, college selection tips"}
        url="/blog"
        type="website"
        pageKey="blog"
      />
      <div className="container py-5">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-dark mb-3">
            {selectedCategory !== 'all' ? `${selectedCategory} Articles` : 'College Guidance Blog'}
          </h1>
          <p className="lead text-muted">
            {selectedCategory !== 'all' 
              ? `Expert insights and tips about ${selectedCategory.toLowerCase()}`
              : 'Expert tips, admission insights, and guidance for your college journey'
            }
          </p>
        </div>

        {/* Search and Filters */}
        <div className="card border-0 shadow-lg mb-5">
          <div className="card-body p-4">
            <form onSubmit={handleSearch}>
              <div className="row g-3">
                <div className="col-lg-6">
                  <div className="position-relative">
                    <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={20} />
                    <input
                      type="text"
                      placeholder="Search blog posts..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="form-control form-control-lg ps-5"
                    />
                  </div>
                </div>
                <div className="col-lg-4">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="form-select form-select-lg"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-2">
                  <button type="submit" className="btn btn-primary btn-lg w-100">
                    Search
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-muted">
            Showing {blogPosts.length} article{blogPosts.length !== 1 ? 's' : ''}
            {selectedCategory !== 'all' && ` in ${selectedCategory}`}
            {searchQuery && ` for "${searchQuery}"`}
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="row g-4">
          {blogPosts.map((post) => (
            <div key={post.id} className="col-lg-4 col-md-6">
              <Link to={`/blog/${post.slug}`} className="card h-100 border-0 shadow-lg text-decoration-none blog-card">
                <div className="position-relative">
                  <OptimizedImage
                    src={post.image_url}
                    alt={`${post.title} - ${post.category} guidance article by ${post.author}`}
                    title={`${post.title} by ${post.author} | ${post.read_time}`}
                    className="card-img-top"
                    style={{ height: '200px', objectFit: 'cover' }}
                    loading="lazy"
                    fallbackSrc="https://images.pexels.com/photos/5212324/pexels-photo-5212324.jpeg?auto=compress&cs=tinysrgb&w=400"
                  />
                  <div className="position-absolute top-0 end-0 m-3">
                    <span className="badge bg-primary fs-6 px-3 py-2">{post.category}</span>
                    {post.featured && <span className="badge bg-warning fs-6 px-3 py-2 ms-2">Featured</span>}
                  </div>
                </div>

                <div className="card-body p-4">
                  <h2 className="h5 fw-bold text-dark mb-3 line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-muted line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>

                  <div className="d-flex align-items-center gap-3 mb-3">
                    {post.author_image && (
                      <OptimizedImage
                        src={post.author_image}
                        alt={`${post.author} - Author profile picture`}
                        title={`${post.author} - Education Expert at Grad Vikalp`}
                        className="rounded-circle"
                        style={{ width: "40px", height: "40px", objectFit: "cover" }}
                        loading="lazy"
                      />
                    )}
                    <div>
                      <div className="fw-semibold text-dark small">{post.author}</div>
                      <div className="d-flex align-items-center gap-2 text-muted small">
                        <Calendar size={12} />
                        <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <Clock size={12} />
                        <span>{post.read_time}</span>
                      </div>
                    </div>
                  </div>

                  {post.tags && post.tags.length > 0 && (
                    <div className="mb-3">
                      <div className="d-flex flex-wrap gap-1">
                        {post.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="badge bg-light text-dark small">
                            {tag}
                          </span>
                        ))}
                        {post.tags.length > 3 && (
                          <span className="badge bg-light text-dark small">
                            +{post.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-primary fw-semibold small">Read Article</span>
                    <ArrowRight size={16} className="text-primary" />
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {blogPosts.length === 0 && (
          <div className="text-center py-5">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: '600px' }}>
              <div className="card-body p-5">
                <div className="mb-4">
                  <Tag className="text-muted mx-auto mb-3" size={64} />
                </div>
                <h3 className="h4 text-dark mb-3">No articles found</h3>
                <p className="text-muted mb-4">
                  {searchQuery || selectedCategory !== 'all'
                    ? 'No articles match your search criteria. Try adjusting your search terms or category filter.'
                    : 'No blog posts have been published yet. Check back soon for expert guidance and tips!'
                  }
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  {(searchQuery || selectedCategory !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('all');
                      }}
                      className="btn btn-outline-secondary"
                    >
                      Clear Filters
                    </button>
                  )}
                  <Link to="/" className="btn btn-primary">
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Featured Categories */}
        {selectedCategory === 'all' && blogPosts.length > 0 && (
          <div className="mt-5">
            <div className="text-center mb-4">
              <h2 className="h3 fw-bold text-dark mb-3">Browse by Category</h2>
              <p className="text-muted">Find articles specific to your interests</p>
            </div>
            <div className="row g-3">
              {categories.slice(1).map((category) => (
                <div key={category} className="col-lg-2 col-md-3 col-sm-4 col-6">
                  <Link
                    to={`/blog?category=${category}`}
                    className="card border-0 bg-light text-decoration-none h-100 category-card"
                  >
                    <div className="card-body p-3 text-center">
                      <h3 className="h6 fw-semibold text-dark mb-1">{category}</h3>
                      <p className="small text-muted mb-0">
                        {blogPosts.filter(post => post.category === category).length} articles
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-5">
          <div className="card bg-primary text-white border-0">
            <div className="card-body p-4 text-center">
              <h2 className="h3 fw-bold mb-3">Ready to Find Your Dream College?</h2>
              <p className="mb-4">Get personalized college recommendations based on your profile.</p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <Link to="/neet-college-predictor" className="btn btn-light text-primary fw-semibold">
                  Get NEET Predictions
                </Link>
                <Link to="/josaa-personalized-list" className="btn btn-outline-light">
                  Get JEE Predictions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;